import React, { useCallback, useContext, useMemo, useState } from 'react';
import { onUnloadPage } from 'common/hooks/unloadActions';
import DialogContext from 'context/DialogContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { DialogAction, UnsavedChangesProviderProps } from './UnsavedChangesProvider.props';

export const UnsavedChangesProvider = ({ children }: UnsavedChangesProviderProps) => {
  const [action, setAction] = useState<DialogAction | false>(false);
  const [isVisible, setIsVisible] = useState(false);
  const dialogContext = useContext(DialogContext);

  const setUnsavedAction = useCallback((action: DialogAction | false) => {
    setAction(action);
    onUnloadPage(!!action);
  }, []);

  const cleanAction = useCallback(() => {
    setUnsavedAction(false);
    setIsVisible(false);
  }, [setUnsavedAction]);

  const showDialog = useCallback(
    (callback: () => void) => {
      if (!action) {
        return;
      }

      const { wrapper: Dialog, content: Content, onLeave } = action;

      dialogContext.showDialog({
        wrapper: Dialog,
        content: Content,
        onClose: () => {
          setIsVisible(false);
          // User clicked X - just close the dialog but keep the unsaved changes
          // This means they stay on the page and are still blocked from navigating
        },
        actions: [
          {
            label: 'Leave Anyways',
            variant: 'contained',
            type: 'danger',
            callback: () => {
              if (onLeave) {
                onLeave();
              }
              cleanAction();
              callback();
            },
          },
        ],
      });
      setIsVisible(true);
    },
    [action, cleanAction, dialogContext]
  );

  const providerValue = useMemo(
    () => ({
      action,
      setAction,
      cleanAction,
      showDialog,
      isVisible,
    }),
    [action, cleanAction, showDialog, isVisible]
  );

  return <UnsavedChangesContext.Provider value={providerValue}>{children}</UnsavedChangesContext.Provider>;
};
